// apps/web/src/pages/compras/estorno-devolucao-dialogs.tsx — Fase 13, Plano 24 (EST-10, COMP-03, D-11, D-12; UI-SPEC § Conferência).
// Diálogos destrutivos de um item de NF já recebido: "Estornar entrada" e "Devolver ao fornecedor". O botão vermelho (#dc2626) fica desabilitado sem
// motivo ou com estorno impossível; durante a requisição ele tem spinner + "Recalculando custo médio…" e o diálogo permanece
// aberto em erro; dispensar é sempre "Voltar". A prévia vem do servidor (dry-run do mesmo cálculo, `previa-estorno`) e a escrita
// usa apiMutate (CSRF). A quantidade é FÍSICA (unidade de estoque). O servidor revalida tudo (T-13-24A/B).
import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { PreviaEstornoNota, ResultadoEstornoNota } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Textarea } from '../../components/ui/textarea';
import {
  MENSAGEM_FALHA_ESTORNO,
  MENSAGEM_FALHA_PREVIA,
  MOTIVO_ESTORNO_MAXIMO,
  mensagemErroEstorno,
  textoConfirmacaoEstorno,
  textoEstornoImpossivel,
  textoPrevia,
  textoSucessoDevolucao,
  textoSucessoEstorno,
  queryPreviaEstorno,
  urlItemNota,
  validarDevolucao,
  type ModoDevolucao,
} from '../../lib/compras/estorno';
import { apiMutate } from '../../lib/csrf-fetch';
import { formatarQuantidade } from '../../lib/vendas/formatacao';

/** Item recebido sobre o qual o diálogo age. */
export interface ItemEstornoAlvo {
  readonly notaCompraItemId: string;
  /** Código da matéria-prima (aparece na mensagem de estorno impossível). */
  readonly codigoMaterial: string;
  readonly unidade: string;
  /** Física que ainda pode ser devolvida (recebida - já estornada). */
  readonly disponivelFisico: number;
}

interface DialogoEstornoBase {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly notaId: string;
  readonly item: ItemEstornoAlvo | null;
  /** Sucesso: a página recarrega a nota e mostra a mensagem (role="status"). */
  readonly onConcluido: (mensagem: string) => void;
  /** Falha: a página mostra o toast (o diálogo continua aberto, com a mensagem inline). */
  readonly onFalha: (mensagem: string) => void;
}

export interface EstadoPrevia {
  readonly previa: PreviaEstornoNota | null;
  readonly carregando: boolean;
  readonly erro: string | null;
}

/**
 * Busca a prévia do servidor ao abrir (e quando a quantidade muda). `quantidade` null = todo o restante; `pular` não consulta
 * (quantidade parcial ainda inválida) e limpa a prévia anterior para que ela não seja confirmada por engano.
 */
export function usePreviaEstorno(
  aberto: boolean,
  notaId: string,
  itemId: string | null,
  quantidade: number | null,
  pular: boolean,
  atrasoMs: number,
): EstadoPrevia {
  const [estado, setEstado] = useState<EstadoPrevia>({ previa: null, carregando: false, erro: null });

  useEffect(() => {
    if (!aberto || !itemId || pular) {
      setEstado({ previa: null, carregando: false, erro: null });
      return;
    }
    let vivo = true;
    const controle = new AbortController();
    setEstado({ previa: null, carregando: true, erro: null });
    const temporizador = window.setTimeout(() => {
      fetch(`${urlItemNota(notaId, itemId)}/previa-estorno${queryPreviaEstorno(quantidade)}`, { credentials: 'include', signal: controle.signal })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return (await res.json()) as PreviaEstornoNota;
        })
        .then((previa) => {
          if (vivo) setEstado({ previa, carregando: false, erro: null });
        })
        .catch(() => {
          if (vivo) setEstado({ previa: null, carregando: false, erro: MENSAGEM_FALHA_PREVIA });
        });
    }, atrasoMs);
    return () => {
      vivo = false;
      controle.abort();
      window.clearTimeout(temporizador);
    };
  }, [aberto, notaId, itemId, quantidade, pular, atrasoMs]);

  return estado;
}

/** Texto do bloqueio quando o servidor diz que a operação é impossível. */
export function textoBloqueioPrevia(previa: PreviaEstornoNota, codigoMaterial: string): string {
  if (previa.motivoImpossivel?.codigo === 'ESTORNO_SALDO_INSUFICIENTE') {
    return textoEstornoImpossivel(codigoMaterial, previa.localizacaoNome, previa.saldoNoLocal, previa.unidadeEstoque);
  }
  return previa.motivoImpossivel?.mensagem ?? 'Não é possível concluir esta operação.';
}

export function BlocoPrevia({ estado, codigoMaterial }: { readonly estado: EstadoPrevia; readonly codigoMaterial: string }) {
  if (estado.carregando) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Calculando o impacto no custo médio…
      </p>
    );
  }
  if (estado.erro) return <FormMessage>{estado.erro}</FormMessage>;
  const previa = estado.previa;
  if (!previa) return null;
  if (!previa.possivel) {
    return (
      <div role="alert" className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <p>{textoBloqueioPrevia(previa, codigoMaterial)}</p>
      </div>
    );
  }
  return (
    <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm tabular-nums text-slate-800">
      {textoPrevia(previa.custoMedioAtual, previa.custoMedioNovo, previa.unidadeEstoque, previa.movimentosAfetados)}
    </p>
  );
}

/** Envia o comando e interpreta o resultado; centraliza spinner, erro inline e toast da página. */
export function useEnvioEstorno(base: DialogoEstornoBase, comando: 'estornar' | 'devolver', textoSucesso: (afetados: number) => string) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (base.aberto) setErro(null);
  }, [base.aberto]);

  async function enviar(corpo: { readonly motivo: string; readonly quantidade?: number | null }): Promise<void> {
    if (!base.item || enviando) return;
    setErro(null);
    setEnviando(true);
    try {
      const res = await apiMutate(`${urlItemNota(base.notaId, base.item.notaCompraItemId)}/${comando}`, { method: 'POST', body: corpo });
      const resposta: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const mensagem = mensagemErroEstorno(res.status, resposta, base.item.codigoMaterial);
        setErro(mensagem);
        base.onFalha(mensagem);
        return;
      }
      const resultado = resposta as ResultadoEstornoNota;
      base.onConcluido(textoSucesso(resultado.movimentosRecalculados));
      base.onAbertoChange(false);
    } catch {
      setErro(MENSAGEM_FALHA_ESTORNO);
      base.onFalha(MENSAGEM_FALHA_ESTORNO);
    } finally {
      setEnviando(false);
    }
  }

  return { enviando, erro, enviar };
}

export function CampoMotivo({
  id,
  valor,
  onChange,
  desabilitado,
}: {
  readonly id: string;
  readonly valor: string;
  readonly onChange: (texto: string) => void;
  readonly desabilitado: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>Motivo (obrigatório)</Label>
      <Textarea id={id} value={valor} onChange={(e) => onChange(e.target.value)} maxLength={MOTIVO_ESTORNO_MAXIMO} disabled={desabilitado} rows={3} />
    </div>
  );
}

export function BotaoConfirmarDestrutivo({
  rotulo,
  habilitado,
  enviando,
  onClick,
}: {
  readonly rotulo: string;
  readonly habilitado: boolean;
  readonly enviando: boolean;
  readonly onClick: () => void;
}) {
  return (
    <Button type="button" variant="destructive" onClick={onClick} disabled={!habilitado || enviando}>
      {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
      {enviando ? 'Recalculando custo médio…' : rotulo}
    </Button>
  );
}

/** "Estornar entrada" (D-11): remove do estoque o restante da entrada e recalcula o custo médio em cadeia. */
export function EstornarEntradaDialog(props: DialogoEstornoBase) {
  const { aberto, onAbertoChange, notaId, item } = props;
  const [motivo, setMotivo] = useState('');
  const { previa, carregando, erro: erroPrevia } = usePreviaEstorno(aberto, notaId, item?.notaCompraItemId ?? null, null, false, 0);
  const { enviando, erro, enviar } = useEnvioEstorno(props, 'estornar', textoSucessoEstorno);

  useEffect(() => {
    if (aberto) setMotivo('');
  }, [aberto]);

  const podeConfirmar = motivo.trim().length > 0 && previa !== null && previa.possivel && !carregando;
  const corpo = previa
    ? textoConfirmacaoEstorno(previa.quantidadeEstornoFisica, previa.unidadeEstoque, previa.movimentosAfetados)
    : 'Estornar esta entrada remove a quantidade do estoque e recalcula o custo médio dos movimentos posteriores. O motivo fica registrado na auditoria.';

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (enviando ? undefined : onAbertoChange(proximo))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Estornar entrada</DialogTitle>
          <DialogDescription>{corpo}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <CampoMotivo id="motivo-estornar-entrada" valor={motivo} onChange={setMotivo} desabilitado={enviando} />
          <BlocoPrevia estado={{ previa, carregando, erro: erroPrevia }} codigoMaterial={item?.codigoMaterial ?? ''} />
          {erro && <FormMessage>{erro}</FormMessage>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <BotaoConfirmarDestrutivo
            rotulo="Estornar entrada"
            habilitado={podeConfirmar}
            enviando={enviando}
            onClick={() => void enviar({ motivo: motivo.trim() })}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Espera depois da última digitação antes de pedir a prévia da quantidade parcial. */
const ATRASO_PREVIA_PARCIAL_MS = 300;

/** "Devolver ao fornecedor" (D-12): total ou parcial; a prévia é recarregada quando a quantidade parcial muda. */
export function DevolverFornecedorDialog(props: DialogoEstornoBase) {
  const { aberto, onAbertoChange, notaId, item } = props;
  const [modo, setModo] = useState<ModoDevolucao>('TOTAL');
  const [quantidadeTexto, setQuantidadeTexto] = useState('');
  const [motivo, setMotivo] = useState('');
  const disponivel = item?.disponivelFisico ?? 0;
  const unidade = item?.unidade ?? '';

  useEffect(() => {
    if (!aberto) return;
    setModo('TOTAL');
    setQuantidadeTexto('');
    setMotivo('');
  }, [aberto]);

  const validacao = validarDevolucao({ modo, quantidade: quantidadeTexto, disponivel, motivo, unidade });
  // Parcial ainda inválida: não consulta (e limpa a prévia anterior) para não confirmar um valor que não é o digitado.
  const quantidadePrevia = modo === 'PARCIAL' ? validacao.quantidade : null;
  const pularPrevia = modo === 'PARCIAL' && validacao.quantidade === null;
  const { previa, carregando, erro: erroPrevia } = usePreviaEstorno(
    aberto,
    notaId,
    item?.notaCompraItemId ?? null,
    quantidadePrevia,
    pularPrevia,
    modo === 'PARCIAL' ? ATRASO_PREVIA_PARCIAL_MS : 0,
  );
  const { enviando, erro, enviar } = useEnvioEstorno(props, 'devolver', textoSucessoDevolucao);

  const podeConfirmar = validacao.valido && previa !== null && previa.possivel && !carregando;
  const erroQuantidade = modo === 'PARCIAL' && quantidadeTexto.trim() !== '' ? validacao.erroQuantidade : null;

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (enviando ? undefined : onAbertoChange(proximo))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Devolver ao fornecedor</DialogTitle>
          <DialogDescription>
            A mercadoria devolvida sai do estoque e o custo médio é recalculado como se essa parte nunca tivesse entrado. A nota de devolução continua sendo emitida fora, na Omie.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <fieldset className="flex flex-col gap-2" disabled={enviando}>
            <legend className="mb-1 text-sm font-medium">Quantidade a devolver</legend>
            <RadioGroup value={modo} onValueChange={(valor) => setModo(valor as ModoDevolucao)} className="gap-2">
              <div className="flex min-h-[var(--control-height,2rem)] items-center gap-2">
                <RadioGroupItem value="TOTAL" id="devolucao-total" />
                <Label htmlFor="devolucao-total" className="tabular-nums">
                  Total ({formatarQuantidade(disponivel)} {unidade})
                </Label>
              </div>
              <div className="flex min-h-[var(--control-height,2rem)] items-center gap-2">
                <RadioGroupItem value="PARCIAL" id="devolucao-parcial" />
                <Label htmlFor="devolucao-parcial">Parcial</Label>
              </div>
            </RadioGroup>
            {modo === 'PARCIAL' && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="devolucao-quantidade" className="text-xs font-medium text-slate-600">
                  Quantidade parcial ({unidade})
                </Label>
                <Input
                  id="devolucao-quantidade"
                  inputMode="decimal"
                  value={quantidadeTexto}
                  onChange={(e) => setQuantidadeTexto(e.target.value)}
                  aria-invalid={erroQuantidade !== null}
                  className="text-right tabular-nums"
                  autoFocus
                />
                {erroQuantidade && <FormMessage>{erroQuantidade}</FormMessage>}
              </div>
            )}
          </fieldset>
          <CampoMotivo id="motivo-devolver-fornecedor" valor={motivo} onChange={setMotivo} desabilitado={enviando} />
          <BlocoPrevia estado={{ previa, carregando, erro: erroPrevia }} codigoMaterial={item?.codigoMaterial ?? ''} />
          {erro && <FormMessage>{erro}</FormMessage>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <BotaoConfirmarDestrutivo
            rotulo="Devolver ao fornecedor"
            habilitado={podeConfirmar}
            enviando={enviando}
            onClick={() => void enviar({ motivo: motivo.trim(), quantidade: validacao.quantidade })}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
